import { PushpinOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Input, Row, Space } from 'antd';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import { api } from 'app/api';
import MapMenu, { MapMenuRow } from 'app/components/MapMenu/MapMenu';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { useMapSlice } from 'app/containers/Map/slice';
import { selectHiddenOnMapGpsVehicleIds } from 'app/containers/Map/slice/selectors';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import { LatLngTuple } from 'leaflet';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { useGetAllRoutesFromState } from 'utils/hooks/useGetAllRoutes';
import { TextRegular12 } from '../../../../../../../styles/typography';
import { RouteConstants } from '../../../../../../routes';
import ZoomOnTransportRoutesButton from './parts/ZoomOnTransportRoutesButton/ZoomOnTransportRoutesButton';

const StyledTransportColorCircle = styled.div<{ $color?: string }>`
  width: 15px;
  height: 15px;
  background: ${({ $color = '#000' }) => $color};
  border-radius: 50%;
`;

interface IProps {}

let lastCenteredTransportRoute: null | {
  gpsVehicleId: number;
  routeIndex: number;
} = null;

const TransportsMenu: FC<IProps> = () => {
  const showZoomOnTransportRoutesButton = Boolean(
    useRouteMatch(RouteConstants.field.list),
  );

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mapActions = useMapSlice().actions;

  const seasonId = useSelector(selectSelectedSeasonId) as number;
  const envId = useSelector(selectSelectedEnvId) as number;

  const hiddenGpsVehicleIds = useSelector(selectHiddenOnMapGpsVehicleIds);

  const [search, setSearch] = useState('');

  const {
    data: transportResponse,
  } = api.endpoints.getTransportPosition.useQueryState({
    seasonId,
    envId,
  });
  const { routes } = useGetAllRoutesFromState();

  const transportsWithItsRoutes =
    transportResponse?.features.map(transport => {
      const thisTransportRoutes = routes.filter(
        route => route.gpsVehicle.gpsId === transport.properties.gps_id,
      );

      return { transport, thisTransportRoutes };
    }) || [];

  const transportWithSearch = transportsWithItsRoutes.filter(
    ({ transport }) => {
      const query = search.toLowerCase().trim();

      if (!query.length) {
        return true;
      }

      return Boolean(
        (transport.properties.model &&
          transport.properties.model.toLowerCase().includes(query)) ||
          transport.properties.license_plate.toLowerCase().includes(query),
      );
    },
  );

  const onZoomClick = (coordinates: LatLngTuple) => {
    dispatch(mapActions.setFitCoordArrOnMap([coordinates]));
  };

  const onCheckboxClick = (gpsVehicleId: number) => {
    dispatch(mapActions.toggleHiddenOnMapByGpsVehicleId(gpsVehicleId));
  };

  return (
    <MapMenu title={t('Transport.ActiveTransport', 'Active transport')}>
      <Input
        prefix={<SearchOutlined />}
        placeholder={t('Common.Search', 'Search')}
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{
          margin: '0 20px 10px',
          width: 'calc(100% - 40px)',
        }}
      />
      <TextRegular12
        style={{
          margin: '0 20px',
        }}
      >
        {`${t('Common.Displayed', 'Displayed: ')} ${
          transportWithSearch?.length ?? ''
        }`}
      </TextRegular12>

      {transportWithSearch?.map(({ transport, thisTransportRoutes }) => (
        <MapMenuRow key={transport.properties.gps_vehicle_id}>
          <Row justify="space-between" align="middle" style={{ flex: 1 }}>
            <Checkbox
              onChange={() =>
                onCheckboxClick(transport.properties.gps_vehicle_id)
              }
              checked={
                !hiddenGpsVehicleIds.includes(
                  transport.properties.gps_vehicle_id,
                )
              }
            >
              {`${transport.properties.model ?? '-'} (${
                transport.properties.license_plate
              })`}
            </Checkbox>
            <Space>
              <StyledTransportColorCircle $color={transport.properties.color} />

              <Button
                onClick={() => {
                  dispatch(
                    mapActions.setHighlightedRoutesGpsVehicleId(
                      transport.properties.gps_vehicle_id,
                    ),
                  );
                  onZoomClick([
                    transport.geometry.coordinates[1],
                    transport.geometry.coordinates[0],
                  ]);
                }}
                shape="circle"
                type="dashed"
                icon={<PushpinOutlined style={{ color: '#40A9FF' }} />}
              />

              {showZoomOnTransportRoutesButton && (
                <ZoomOnTransportRoutesButton
                  gpsVehicleId={transport.properties.gps_vehicle_id}
                  lastCenteredTransportRoute={lastCenteredTransportRoute}
                  thisTransportRoutes={thisTransportRoutes}
                />
              )}
            </Space>
          </Row>
        </MapMenuRow>
      ))}
    </MapMenu>
  );
};

export default TransportsMenu;
